// SNOWCLUB-V101-ANALYTICS-DESIGN-CART-20260930-001 — GA4 add_to_cart / remove_from_cart for the design bundle's own
// cart. The bundle is a standalone compiled logic module that cannot import host code, so it ANNOUNCES each real
// user add/remove over the same-origin `postMessage` seam it already uses for `sg:signin` / `sg:signout`
// (`{ type: "sg:cart", action, lines }`, see SG_CART_EVENT in the design source), and ExportSessionBridge hands the
// message here. Every send goes through @/lib/analytics-event, so the production-host and initialized-tag
// gates are identical to every other event; denied analytics storage uses consent-aware cookieless measurement.
//
// The bundle only announces a genuine new addition or removal — never a restore, a replacement, a quantity
// change or a refused action — and only whitelisted line fields travel, so nothing here can carry a person.
import { gaItemFromDesignLine, trackAddToCart, trackRemoveFromCart } from "@/lib/analytics-event";

interface DesignCartLine {
  id: string;
  name?: string;
  cat?: string;
  vendor?: string;
  qty: number;
  /** Per-unit line price: price × qty is the line total the cart shows. */
  price: number;
}

function readLines(raw: unknown): DesignCartLine[] {
  if (!Array.isArray(raw)) return [];
  const lines: DesignCartLine[] = [];
  for (const l of raw) {
    if (!l || typeof l !== "object") continue;
    const r = l as Record<string, unknown>;
    if (typeof r.id !== "string" || !r.id) continue;
    lines.push({
      id: r.id,
      name: typeof r.name === "string" && r.name ? r.name : undefined,
      cat: typeof r.cat === "string" && r.cat ? r.cat : undefined,
      vendor: typeof r.vendor === "string" && r.vendor ? r.vendor : undefined,
      qty: typeof r.qty === "number" && Number.isFinite(r.qty) && r.qty > 0 ? r.qty : 1,
      // `price` is already the per-unit LINE price (a rental's rate × its inclusive days, computed by SG_CART_EVENT
      // from SG_LINE_DAYS), so it is used as-is — never multiplied by days again here, and the host never sees dates.
      // A line with no published fare (or a non-finite one) reports price 0 (GA4 needs a number) — the same rule as
      // the host cart. Rounded to cents so value = Σ quantity × price carries no float tail.
      price: typeof r.price === "number" && Number.isFinite(r.price) ? Math.round(r.price * 100) / 100 : 0,
    });
  }
  return lines;
}

/** Handle one `sg:cart` message. Returns how many GA4 events were attempted (0 for anything malformed). */
export function handleDesignCartMessage(data: unknown): number {
  const d = data as { type?: unknown; action?: unknown; lines?: unknown } | null;
  if (d?.type !== "sg:cart") return 0;
  const lines = readLines(d.lines);
  if (lines.length === 0) return 0;
  if (d.action === "add") {
    // One add is one line; GA4 add_to_cart carries the line that was added.
    for (const l of lines) trackAddToCart(gaItemFromDesignLine(l));
    return lines.length;
  }
  if (d.action === "remove") {
    trackRemoveFromCart(lines.map(gaItemFromDesignLine));
    return 1;
  }
  return 0;
}
